import { Component, HostListener, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { FrontlineOpsService } from "src/app/Services/frontline-ops.service";
import { VersionHistoryPanelComponent } from "../version-history-panel/version-history-panel.component";
import { ScheduleViewDialogComponent } from "../schedule-view-dialog/schedule-view-dialog.component";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { ToastrService } from "ngx-toastr";
import { GuideQuestionFormComponent } from "src/app/shared/guide-questions/guide-question-form.component";
import {
  GUIDE_END_FORM_KEY,
  GuideQuestionForm,
  isGuideQuestionFormActive,
  parseGuideQuestionForm,
} from "src/app/models/guide-question-form.model";

const VIDEO_EXTENSIONS = ["mp4", "mov", "wmv", "flv", "avi", "mkv", "webm", "m4v", "3gp"];

/**
 * NEW-10 Mobile task mode — one primary action per screen for frontline workers.
 */
@Component({
  selector: "app-perform-task",
  templateUrl: "./perform-task.component.html",
  styleUrls: ["./perform-task.component.scss"],
})
export class PerformTaskComponent implements OnInit {
  rowKey = "";
  title = "";
  /** Guide pack this task was opened from; exiting returns there. */
  packRowKey = "";
  flow: any = null;
  steps: any[] = [];
  screen: "cover" | "step" | "done" = "cover";
  /** End-of-guide form shown on the last step after that step's own form is saved. */
  showingEndForm = false;
  /** Step check screen: the step's questions, opened by "Next step" after the worker reads the step. */
  showingStepForm = false;
  reportingIssue = false;
  saveNotice = "";
  runError = "";
  runIssues: { stepTitle: string; description: string }[] = [];
  private openRun: any = null;
  private openRunIssues: { stepTitle: string; description: string }[] = [];
  /** Version the server recorded on this run; responses and issues must carry the same one. */
  private runFlowVersion = 0;
  @ViewChild(GuideQuestionFormComponent) questionForm?: GuideQuestionFormComponent;
  private endQuestionForm: GuideQuestionForm | null = null;
  /** Form keys answered (or skipped) in this run, so Back then Next doesn't ask again. */
  private answeredForms = new Set<string>();
  stepIndex = 0;
  furthestIndex = 0;
  loading = true;
  loadError = false;
  checklist: any = null;
  ticked: Record<string, boolean> = {};
  acknowledging = false;
  acknowledged = false;

  /** Client session for start/end timing (set on Start task). */
  private sessionId = "";
  private startedAtUtc: Date | null = null;
  private sessionSaved = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private flowService: FlowServiceService,
    private frontlineOps: FrontlineOpsService,
    private authModal: AuthModalService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.rowKey = this.route.snapshot.paramMap.get("rowKey") || "";
    this.title = this.route.snapshot.paramMap.get("title") || "Guide";
    this.packRowKey = this.route.snapshot.queryParamMap.get("pack") || "";
    this.load();
  }

  private get flowVersion(): number {
    return this.runFlowVersion || this.flow?.version || this.flow?.Version || 1;
  }

  private setRunVersion(session: any): void {
    const version = Number(session?.flowVersion ?? session?.FlowVersion);
    this.runFlowVersion = version > 0 ? version : 0;
  }

  private toRunIssue(issue: any): { stepTitle: string; description: string } {
    const index = issue?.stepIndex ?? issue?.StepIndex;
    return {
      stepTitle:
        issue?.stepTitle ||
        issue?.StepTitle ||
        (typeof index === "number" ? `Step ${index + 1}` : "Task"),
      description: issue?.issueDescription || issue?.IssueDescription || "Issue reported",
    };
  }

  private getLoggedInUser(): any {
    try {
      return JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    } catch {
      return null;
    }
  }

  private resolveUserName(user: any): string {
    if (!user) return "";
    const full = `${user.firstName || user.FirstName || ""} ${
      user.lastName || user.LastName || ""
    }`.trim();
    return (
      full ||
      user.userName ||
      user.UserName ||
      user.emailAddress ||
      user.EmailAddress ||
      ""
    );
  }

  private load(): void {
    this.loading = true;
    this.loadError = false;
    const user = this.getLoggedInUser();
    this.flowService.getFlowById(this.rowKey, user?.emailAddress || "").subscribe({
      next: (data: any) => {
        const payload = data?.flows ?? data?.Flows ?? (Array.isArray(data) ? data[0] : data);
        if (!payload) {
          this.loading = false;
          this.loadError = true;
          return;
        }
        this.flow = payload;
        const rawSteps = payload.steps || payload.Steps || [];
        this.steps = (Array.isArray(rawSteps) ? rawSteps : []).map((s: any) =>
          this.normalizeStep(s)
        );
        const endForm = parseGuideQuestionForm(payload.endQuestionForm ?? payload.EndQuestionForm);
        this.endQuestionForm = isGuideQuestionFormActive(endForm) ? endForm : null;
        this.loading = false;
        this.loadOpenRun();
        this.frontlineOps.listChecklists(this.rowKey).subscribe({
          next: (list) => {
            const approved = (list || []).find(
              (c) => (c.status || c.Status) === "Approved"
            );
            this.checklist = approved || null;
          },
          error: () => {},
        });
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  private normalizeStep(step: any): any {
    const url = `${step?.url || step?.Url || step?.mediaUrl || step?.MediaUrl || ""}`.trim();
    const poster = `${step?.videoThumbNailUrl || step?.VideoThumbNailUrl || ""}`.trim();
    const questionForm = parseGuideQuestionForm(step?.questionForm ?? step?.QuestionForm);
    return {
      rowKey: step?.rowKey || step?.RowKey || "",
      questionForm: isGuideQuestionFormActive(questionForm) ? questionForm : null,
      title: step?.title || step?.Title || "",
      description: step?.description || step?.Description || "",
      url,
      poster,
      isVideo: this.isVideoUrl(url),
      isSafetyCritical: !!(step?.isSafetyCritical ?? step?.IsSafetyCritical),
      safetyTitle: step?.safetyInstructionsTitle || step?.SafetyInstructionsTitle || "",
      safetyDescription:
        step?.safetyInstructionsDescription || step?.SafetyInstructionsDescription || "",
    };
  }

  private isVideoUrl(url: string): boolean {
    const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
    return VIDEO_EXTENSIONS.includes(ext);
  }

  private recordEvent(eventType: string, stepIndex?: number): void {
    this.frontlineOps
      .recordOpsEvent({
        flowId: this.rowKey,
        eventType,
        stepIndex,
        flowVersion: this.flowVersion,
      })
      .subscribe({ error: () => {} });
  }

  private newSessionId(): string {
    return typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }

  private loadOpenRun(): void {
    const user = this.getLoggedInUser();
    if (!user?.emailAddress && !user?.EmailAddress) return;
    this.frontlineOps.getOpenTaskRun(this.rowKey).subscribe({
      next: (state) => {
        const session = state?.session || (state as any)?.Session;
        if (!session) return;
        this.openRun = session;
        const keys = state?.passedFormKeys || (state as any)?.PassedFormKeys || [];
        keys.forEach((key: string) => this.answeredForms.add(key));
        const issues = state?.issues || (state as any)?.Issues || [];
        this.openRunIssues = issues.map((issue: any) => this.toRunIssue(issue));
      },
      error: () => {},
    });
  }

  /** Creates the run before any completion is recorded. A failed save stays on the cover. */
  private beginTaskSession(onReady: () => void): void {
    const user = this.getLoggedInUser();
    const email = user?.emailAddress || user?.EmailAddress || "";
    if (!email) {
      this.authModal.openModal("login", this.router.url);
      this.runError = "Sign in before this task can be recorded.";
      return;
    }
    this.sessionId = this.newSessionId();
    this.startedAtUtc = new Date();
    this.sessionSaved = false;
    this.runError = "";
    this.runFlowVersion = 0;
    this.frontlineOps
      .recordTaskSession({
        flowId: this.rowKey,
        sessionId: this.sessionId,
        userName: this.resolveUserName(user),
        startedAtUtc: this.startedAtUtc.toISOString(),
        endedAtUtc: this.startedAtUtc.toISOString(),
        status: "started",
      })
      .subscribe({
        next: (session) => {
          this.setRunVersion(session);
          this.recordEvent("Start");
          onReady();
        },
        error: (err) => {
          this.sessionId = "";
          this.startedAtUtc = null;
          this.runError = err?.error?.error || "The task could not be started. Nothing was recorded.";
        },
      });
  }

  private saveTaskSession(onDone?: () => void): void {
    const user = this.getLoggedInUser();
    const email = user?.emailAddress || user?.EmailAddress || "";
    if (!email || !this.startedAtUtc) {
      this.runError = "Sign in before this task can be recorded.";
      return;
    }
    if (this.sessionSaved) {
      onDone?.();
      return;
    }

    const ended = new Date();
    this.frontlineOps
      .recordTaskSession({
        flowId: this.rowKey,
        sessionId: this.sessionId,
        userName: this.resolveUserName(user),
        startedAtUtc: this.startedAtUtc.toISOString(),
        endedAtUtc: ended.toISOString(),
        status: "completed",
      })
      .subscribe({
        next: () => {
          this.sessionSaved = true;
          this.openRun = null;
          onDone?.();
        },
        error: (err) => {
          this.acknowledging = false;
          this.runError = err?.error?.error || "The task was not completed because the save failed.";
          this.toastr.error(this.runError);
        },
      });
  }

  get flowTitle(): string {
    return this.flow?.title || this.flow?.Title || this.title.replace(/-/g, " ");
  }

  get coverImage(): string {
    const url = this.flow?.url || this.flow?.Url || "";
    const poster = this.flow?.videoThumbNailUrl || this.flow?.VideoThumbNailUrl || "";
    return url && !this.isVideoUrl(url) ? url : poster;
  }

  get coverSafety(): { title: string; description: string } | null {
    const on = this.flow?.coverIsSafetyCritical ?? this.flow?.CoverIsSafetyCritical;
    if (!on) return null;
    return {
      title:
        this.flow?.coverSafetyInstructionsTitle ||
        this.flow?.CoverSafetyInstructionsTitle ||
        "Safety instructions",
      description:
        this.flow?.coverSafetyInstructionsDescription ||
        this.flow?.CoverSafetyInstructionsDescription ||
        "",
    };
  }

  get currentStep(): any {
    return this.steps[this.stepIndex] || null;
  }

  get isLastStep(): boolean {
    return this.stepIndex >= this.steps.length - 1;
  }

  get progressPct(): number {
    if (!this.steps.length || this.screen === "cover") return 0;
    if (this.screen === "done") return 100;
    if (this.screen === "step" && this.showingEndForm) return 100;
    return Math.round(((this.stepIndex + 1) / this.steps.length) * 100);
  }

  get taskSessionId(): string | null {
    return this.sessionId || null;
  }

  get taskFlowVersion(): number {
    return this.flowVersion;
  }

  private stepFormKey(index: number): string {
    return this.steps[index]?.rowKey || `step-${index}`;
  }

  /** The current step's form while it still needs a valid response. */
  get pendingStepForm(): { key: string; form: GuideQuestionForm; stepIndex: number } | null {
    if (this.screen !== "step" || this.showingEndForm) return null;
    const step = this.currentStep;
    const stepKey = this.stepFormKey(this.stepIndex);
    if (step?.questionForm && !this.answeredForms.has(stepKey)) {
      return { key: stepKey, form: step.questionForm, stepIndex: this.stepIndex };
    }
    return null;
  }

  /**
   * Form shown right now: the step form on its check screen (opened by "Next step"),
   * or the final form once the last step is done.
   */
  get activeQuestions(): { key: string; form: GuideQuestionForm; stepIndex: number | null } | null {
    if (this.screen !== "step") return null;
    if (this.showingEndForm && this.endQuestionForm && !this.answeredForms.has(GUIDE_END_FORM_KEY)) {
      return { key: GUIDE_END_FORM_KEY, form: this.endQuestionForm, stepIndex: null };
    }
    return this.showingStepForm ? this.pendingStepForm : null;
  }

  get primaryLabel(): string {
    if (this.activeQuestions) return "Save and continue";
    if (this.pendingStepForm) return "Next step";
    return this.isLastStep ? "Finish" : "Next step";
  }

  get issueStepIndex(): number | null {
    if (this.showingEndForm) return this.steps.length ? this.steps.length - 1 : null;
    return this.stepIndex;
  }

  /** Moves past the current step; `notice` is shown on the screen the worker lands on. */
  private advanceFromStep(notice = ""): void {
    this.reportingIssue = false;
    this.showingStepForm = false;
    if (!this.showingEndForm && this.isLastStep && this.endQuestionForm && !this.answeredForms.has(GUIDE_END_FORM_KEY)) {
      this.showingEndForm = true;
      this.saveNotice = notice;
      this.scrollTop();
      return;
    }
    if (!this.isLastStep) {
      this.goToStep(this.stepIndex + 1, notice);
    } else {
      this.screen = "done";
      this.showingEndForm = false;
      this.saveNotice = notice;
      this.scrollTop();
    }
  }

  onQuestionsDone(): void {
    const key = this.activeQuestions?.key;
    if (key) this.answeredForms.add(key);
    this.advanceFromStep("Response saved.");
  }

  saveAndContinue(): void {
    if (this.reportingIssue) return;
    if (this.pendingStepForm && !this.showingStepForm) {
      this.showingStepForm = true;
      this.saveNotice = "";
      this.scrollTop();
      return;
    }
    if (this.activeQuestions) {
      this.questionForm?.submit();
      return;
    }
    this.advanceFromStep();
  }

  toggleIssueReport(): void {
    this.reportingIssue = !this.reportingIssue;
  }

  onIssueSubmitted(issue: any): void {
    const user = this.getLoggedInUser();
    const email = user?.emailAddress || user?.EmailAddress || "";
    if (!email || !this.sessionId) {
      this.authModal.openModal("login", this.router.url);
      this.runError = "Sign in before reporting an issue on this task.";
      return;
    }
    const index = issue?.stepIndex ?? this.issueStepIndex;
    const step = index != null ? this.steps[index] : null;
    this.flowService
      .createFlowReview({
        FlowRowKey: this.rowKey,
        IsCompleted: false,
        HasIssues: true,
        ReviewText: null,
        Rating: 0,
        CreatedByUserEmail: email,
        Issues: [
          {
            ...issue,
            stepIndex: index,
            stepId: this.showingEndForm ? null : step?.rowKey || null,
            stepTitle: this.showingEndForm ? "End of guide" : issue?.stepTitle,
            runId: this.sessionId,
            flowVersion: this.flowVersion,
            workerId: email,
          },
        ],
      })
      .subscribe({
        next: () => {
          this.runIssues.push({
            stepTitle: this.showingEndForm ? "End of guide" : issue?.stepTitle || `Step ${this.stepIndex + 1}`,
            description: issue?.issueDescription || "Issue reported",
          });
          this.saveNotice = "Issue reported. This step is not complete.";
          this.reportingIssue = false;
          this.toastr.success("Issue reported. You are still on this step.");
        },
        error: (err) => {
          this.toastr.error(err?.error?.error || "The issue was not submitted. Your entries are still here.");
        },
      });
  }

  get checklistItems(): any[] {
    return this.checklist?.items || this.checklist?.Items || [];
  }

  itemId(item: any): string {
    return item.itemId || item.ItemId;
  }

  get allTicked(): boolean {
    return this.checklistItems.every((i) => this.ticked[this.itemId(i)]);
  }

  start(): void {
    this.beginTaskSession(() => {
      this.runIssues = [];
      this.saveNotice = "";
      if (!this.steps.length) {
        this.showingEndForm = !!this.endQuestionForm;
        this.screen = this.endQuestionForm ? "step" : "done";
        return;
      }
      this.goToStep(0);
    });
  }

  next(): void {
    if (this.screen === "cover") {
      this.start();
      return;
    }
    if (this.screen !== "step") return;
    this.saveAndContinue();
  }

  prev(): void {
    if (this.reportingIssue) {
      this.reportingIssue = false;
      return;
    }
    if (this.showingStepForm) {
      this.showingStepForm = false;
      this.saveNotice = "";
      this.scrollTop();
      return;
    }
    if (this.showingEndForm) {
      this.showingEndForm = false;
      this.saveNotice = "";
      return;
    }
    if (this.screen === "done") {
      if (this.steps.length) this.goToStep(this.steps.length - 1);
      else this.screen = "cover";
    } else if (this.screen === "step" && this.stepIndex > 0) {
      this.goToStep(this.stepIndex - 1);
    } else if (this.screen === "step") {
      this.screen = "cover";
      this.scrollTop();
    } else {
      this.exit();
    }
  }

  resumeSavedRun(): void {
    const session = this.openRun;
    if (!session) {
      this.resumeFurthest();
      return;
    }
    this.sessionId = session.sessionId || session.SessionId || "";
    const started = session.startedAtUtc || session.StartedAtUtc;
    this.startedAtUtc = started ? new Date(started) : new Date();
    this.sessionSaved = false;
    this.runError = "";
    this.setRunVersion(session);
    this.runIssues = [...this.openRunIssues];
    const restored = "Saved progress restored.";
    this.saveNotice = restored;
    const lastKey = session.lastCompletedFormKey || session.LastCompletedFormKey;
    if (lastKey === GUIDE_END_FORM_KEY) {
      this.screen = "done";
      return;
    }
    const lastStep = Math.max(this.steps.length - 1, 0);
    const savedIndex = session.currentStepIndex ?? session.CurrentStepIndex;
    const lastIndex = session.lastCompletedStepIndex ?? session.LastCompletedStepIndex;
    let index = 0;
    if (typeof savedIndex === "number" && savedIndex >= 0) {
      index = Math.min(savedIndex, lastStep);
    } else if (typeof lastIndex === "number" && lastIndex >= 0) {
      index = Math.min(lastIndex + 1, lastStep);
    }
    // A step whose questions still have no valid response blocks every step after it.
    const pendingIndex = this.steps.findIndex((step, i) => {
      const key = step.rowKey || `step-${i}`;
      return !!step.questionForm && !this.answeredForms.has(key);
    });
    if (pendingIndex >= 0 && pendingIndex < index) index = pendingIndex;
    const stepKey = this.stepFormKey(index);
    const stepDone = !this.steps[index]?.questionForm || this.answeredForms.has(stepKey);
    if (!this.steps.length) {
      this.showingEndForm = !!this.endQuestionForm && !this.answeredForms.has(GUIDE_END_FORM_KEY);
      this.screen = "step";
      return;
    }
    this.goToStep(index, restored);
    if (
      index === this.steps.length - 1 &&
      stepDone &&
      this.endQuestionForm &&
      !this.answeredForms.has(GUIDE_END_FORM_KEY)
    ) {
      this.showingEndForm = true;
    }
  }

  resumeFurthest(): void {
    if (!this.startedAtUtc) {
      this.beginTaskSession(() => this.goToStep(this.furthestIndex));
      return;
    }
    this.goToStep(this.furthestIndex);
  }

  restart(): void {
    this.ticked = {};
    this.acknowledged = false;
    this.furthestIndex = 0;
    this.answeredForms.clear();
    this.showingEndForm = false;
    this.showingStepForm = false;
    this.reportingIssue = false;
    this.saveNotice = "";
    this.runIssues = [];
    this.openRun = null;
    this.openRunIssues = [];
    this.runFlowVersion = 0;
    this.sessionId = "";
    this.startedAtUtc = null;
    this.sessionSaved = false;
    this.screen = "cover";
    this.scrollTop();
  }

  private goToStep(index: number, notice = ""): void {
    this.screen = "step";
    this.showingEndForm = false;
    this.showingStepForm = false;
    this.reportingIssue = false;
    this.saveNotice = notice;
    this.stepIndex = index;
    if (index > this.furthestIndex) this.furthestIndex = index;
    this.recordEvent("Step", index);
    this.saveRunPosition(index);
    this.scrollTop();
  }

  private saveRunPosition(index: number): void {
    if (!this.sessionId || this.sessionSaved) return;
    this.frontlineOps
      .saveTaskRunPosition(this.rowKey, this.sessionId, index)
      .subscribe({ error: () => {} });
  }

  private scrollTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target && /^(INPUT|TEXTAREA|SELECT|VIDEO)$/.test(target.tagName)) return;
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (this.screen !== "cover") this.prev();
    }
  }

  toggleTick(id: string): void {
    this.ticked[id] = !this.ticked[id];
  }

  acknowledge(): void {
    if (this.acknowledged || this.acknowledging) return;
    const user = this.getLoggedInUser();
    if (!user?.emailAddress && !user?.EmailAddress) {
      this.authModal.openModal("login", this.router.url);
      this.runError = "Sign in before this task can be recorded.";
      return;
    }

    this.acknowledging = true;
    this.runError = "";
    this.saveTaskSession(() => {
      if (!this.checklist) {
        this.recordEvent("Complete");
        this.acknowledging = false;
        this.acknowledged = true;
        this.toastr.success("Recorded requirements satisfied");
        return;
      }
      const tickedIds = this.checklistItems
        .map((i) => this.itemId(i))
        .filter((id) => this.ticked[id]);
      this.frontlineOps
        .saveCompletion({
          checklistId: this.checklist.rowKey || this.checklist.RowKey,
          flowId: this.rowKey,
          flowVersion: this.checklist.flowVersion || this.checklist.FlowVersion || 1,
          tickedItemIdsJson: JSON.stringify(tickedIds),
          isAcknowledged: true,
          acknowledgedAtUtc: new Date().toISOString(),
        })
        .subscribe({
          next: () => {
            this.recordEvent("Complete");
            this.acknowledging = false;
            this.acknowledged = true;
            this.toastr.success("Recorded requirements satisfied");
          },
          error: () => {
            this.acknowledging = false;
            this.sessionSaved = false;
            this.toastr.error("Could not save acknowledgement. The task was not marked complete.");
          },
        });
    });
  }

  retry(): void {
    this.load();
  }

  /** `full=1` stops the guide page from sending org end users straight back here. */
  openFullView(): void {
    this.router.navigate(["/guide", this.title || "wi", this.rowKey], {
      queryParams: { full: 1 },
    });
  }

  exit(): void {
    if (this.packRowKey) {
      this.router.navigate(["/view-guide-pack", this.packRowKey]);
      return;
    }
    this.openFullView();
  }

  get guideVersion(): number | null {
    const version = Number(this.flow?.version ?? this.flow?.Version);
    return version > 0 ? version : null;
  }

  /** Task mode's own menu: copying this page's link and scheduling a reminder are always offered. */
  readonly canShareLink = true;
  readonly canSchedule = true;

  openVersionHistory(): void {
    if (!this.rowKey) return;
    const user = this.getLoggedInUser();
    const email = `${user?.emailAddress || user?.EmailAddress || ""}`.toLowerCase();
    const owner = `${this.flow?.customerEmail || this.flow?.CustomerEmail || ""}`.toLowerCase();
    const dialogRef = this.dialog.open(VersionHistoryPanelComponent, {
      width: "640px",
      data: {
        entityType: "flow",
        logicalId: this.rowKey,
        canManageVersions: !!email && email === owner,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result || (result.action !== "restore" && result.action !== "delete")) return;
      // Swapping the guide under an open run would change its steps mid-task.
      if (this.screen === "cover") {
        this.load();
      } else {
        this.toastr.info("The guide changed. Finish or restart this task to use the new version.");
      }
    });
  }

  async copyTaskLink(): Promise<void> {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("textarea");
        input.value = url;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      this.toastr.success("Link copied to clipboard");
    } catch {
      this.toastr.error("The link could not be copied.");
    }
  }

  openSchedule(): void {
    if (!this.getLoggedInUser()) {
      localStorage.setItem("returnUrl", this.router.url);
      this.authModal.openModal("registration", this.router.url);
      return;
    }
    if (!this.flow) return;
    this.dialog.open(ScheduleViewDialogComponent, {
      width: "520px",
      maxWidth: "95vw",
      panelClass: "dialoguebox",
      data: {
        entityType: "flow",
        title: this.flowTitle,
        linkUrl: window.location.href,
      },
    });
  }
}
